import { useState, useRef, useEffect, useCallback } from 'react'
import { useFetch } from '@moonpig/web-core-fetch'
import { logger } from '@moonpig/web-core-monitoring'
import { getAgentMessages, getRestoredMessages } from './mappers'
import { ConnectionStatus } from './types'
import { getConciergeChatUrl } from './helpers'
import { useChatMessagesContext, useChatModeContext } from '../ChatContext'

// Must match the event dispatched from ConciergeButton component
const CONCIERGE_VISIBILITY_CHANGE_EVENT = 'concierge-visibility-change'

export const useChatGateway = () => {
  const { appendMessages, appendErrorMessage, setMessages } =
    useChatMessagesContext()
  const {
    openChatModal,
    switchToChatMode,
    switchToLoadingConversation,
    disableDisclamer,
    resetToStartMode,
  } = useChatModeContext()
  const { fetch } = useFetch()
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.Connected,
  )

  const hasAttemptedRestore = useRef(false)

  const hasLocalInteraction = useRef(false)
  const flagLocalInteractionToGateway = () => {
    hasLocalInteraction.current = true
  }
  const chatUrl = getConciergeChatUrl()

  const restoreSession = useCallback(async () => {
    switchToLoadingConversation()
    try {
      const response = await fetch(chatUrl, {
        method: 'GET',
        credentials: 'include',
      })

      if (!response.ok) {
        logger.fixToday('Failed to restore Concierge Chat session', {
          response: await response.text(),
          code: response.status,
        })
        return
      }

      const payload = await response.json()

      if (hasLocalInteraction.current) {
        return
      }

      const restoredMessages = getRestoredMessages(payload)
      if (restoredMessages.length > 0) {
        disableDisclamer()
        setMessages(restoredMessages)
      }
    } catch (error) {
      logger.fixToday(
        'Concierge Chat session restoration failed',
        undefined,
        error,
      )
    } finally {
      switchToChatMode()
    }
  }, [
    chatUrl,
    fetch,
    switchToLoadingConversation,
    switchToChatMode,
    disableDisclamer,
    setMessages,
  ])

  const sendMessage = async (message: string) => {
    disableDisclamer()
    hasLocalInteraction.current = true
    setConnectionStatus(ConnectionStatus.Sending)

    try {
      const response = await fetch(chatUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          prompt: message,
        }),
        credentials: 'include',
      })

      if (!response.ok) {
        logger.fixToday('Failed to query Concierge Chat', {
          response: await response.text(),
          code: response.status,
        })
        throw new Error('Failed to query Concierge Chat')
      }

      const payload = await response.json()
      const agentMessages = getAgentMessages(payload)
      if (agentMessages.length > 0) {
        appendMessages(agentMessages)
      }
      setConnectionStatus(ConnectionStatus.Connected)
    } catch (error) {
      logger.fixToday('Concierge Chat request failed', undefined, error)
      setConnectionStatus(ConnectionStatus.Error)
      appendErrorMessage()
    }
  }

  const clearSession = async () => {
    try {
      const response = await fetch(chatUrl, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        logger.fixToday('Failed to clear Concierge Chat session', {
          response: await response.text(),
          code: response.status,
        })
      }
    } catch (error) {
      logger.fixToday('Concierge Chat session clear failed', undefined, error)
    }
  }

  const startNewSession = async () => {
    hasLocalInteraction.current = true
    setConnectionStatus(ConnectionStatus.Connected)
    clearSession()
    resetToStartMode()
  }

  const showChatModal = useCallback(() => {
    if (!hasAttemptedRestore.current) {
      hasAttemptedRestore.current = true
      restoreSession()
    }

    openChatModal()
  }, [restoreSession, openChatModal])

  const handleVisibilityChange = useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent<{ isOpen?: boolean }>

      if (!detail?.isOpen) {
        return
      }

      showChatModal()
    },
    [showChatModal],
  )

  useEffect(() => {
    window.addEventListener(
      CONCIERGE_VISIBILITY_CHANGE_EVENT,
      handleVisibilityChange as EventListener,
    )

    return () => {
      window.removeEventListener(
        CONCIERGE_VISIBILITY_CHANGE_EVENT,
        handleVisibilityChange as EventListener,
      )
    }
  }, [handleVisibilityChange])

  return {
    flagLocalInteractionToGateway,
    sendMessage,
    startNewSession,
    connectionStatus,
  }
}
